import { formatDate } from '@angular/common';
import { Injectable } from '@angular/core';
import { UIDateTimeFormat } from '../constants/app-data.constants';

@Injectable({ providedIn: 'root' })
export class DateUtilService {
	public differenceInDays(date1: Date, date2: Date): number {
		return Math.abs(date1.getTime() - date2.getTime()) / (1000 * 60 * 60 * 24);
	}

	public parseDate(value: any): Date | null {
		if (!value) { return null; }

		if (value instanceof Date) {
			return isNaN(value.getTime()) ? null : value;
		}

		if (typeof value === 'string') {
			const trimmed = value.trim();

			// Reject bare numeric strings (e.g., "1212") to avoid false positives
			if (/^\d+$/.test(trimmed)) { return null; }

			// Primary: dd-mm-yyyy
			let match = /^([0-9]{1,2})-([0-9]{1,2})-([0-9]{4})$/.exec(trimmed);
			if (match) {
				const dd = parseInt(match[1], 10);
				const mm = parseInt(match[2], 10);
				const yyyy = parseInt(match[3], 10);
				const d = new Date(yyyy, mm - 1, dd);
				if (d.getFullYear() === yyyy && d.getMonth() === mm - 1 && d.getDate() === dd) {
					return d;
				}
				return null;
			}

			// Fallback: dd/MM/yyyy
			match = /^([0-9]{1,2})\/([0-9]{1,2})\/([0-9]{4})$/.exec(trimmed);
			if (match) {
				const dd = parseInt(match[1], 10);
				const mm = parseInt(match[2], 10);
				const yyyy = parseInt(match[3], 10);
				const d = new Date(yyyy, mm - 1, dd);
				if (d.getFullYear() === yyyy && d.getMonth() === mm - 1 && d.getDate() === dd) {
					return d;
				}
				return null;
			}

			// Final fallback: Strict ISO-like only (yyyy-MM-dd or yyyy-MM-ddTHH:mm:ss...)
			if (/^\d{4}-\d{2}-\d{2}(?:[T\s].*)?$/.test(trimmed)) {
				const iso = new Date(trimmed);
				return isNaN(iso.getTime()) ? null : iso;
			}

			return null;
		}

		return null;
	}

	public isOrderedRange(startValue: any, endValue: any): boolean {
		const start = this.parseDate(startValue);
		const end = this.parseDate(endValue);
		return !!start && !!end && end.getTime() >= start.getTime();
	}

	public rangesOverlap(startA: any, endA: any, startB: any, endB: any): boolean {
		const aStart = this.parseDate(startA);
		const aEnd = this.parseDate(endA);
		const bStart = this.parseDate(startB);
		const bEnd = this.parseDate(endB);
		if (!aStart || !aEnd || !bStart || !bEnd) { return false; }
		return aStart.getTime() < bEnd.getTime() && bStart.getTime() < aEnd.getTime();
	}

	public hasOverlap(items: any[], startField: string, endField: string, ignoreIndex: number = -1): boolean {
		if (!Array.isArray(items) || items.length < 2) { return false; }
		const ranges = items
			.map((x, idx) => ({ idx, start: this.parseDate(x?.[startField]), end: this.parseDate(x?.[endField]) }))
			.filter(r => r.idx !== ignoreIndex && !!r.start && !!r.end)
			.sort((a, b) => (a.start as Date).getTime() - (b.start as Date).getTime());

		for (let i = 1; i < ranges.length; i++) {
			const prev = ranges[i - 1];
			const curr = ranges[i];
			if ((curr.start as Date).getTime() <= (prev.end as Date).getTime()) {
				return true;
			}
		}
		return false;
	}

	public rangeOverlapsAny(startValue: any, endValue: any, items: any[], startField: string, endField: string, ignoreIndex: number = -1): boolean {
		const start = this.parseDate(startValue);
		const end = this.parseDate(endValue);
		if (!start || !end) { return false; }
		return (Array.isArray(items) ? items : [])
			.map((x, idx) => ({ idx, s: this.parseDate(x?.[startField]), e: this.parseDate(x?.[endField]) }))
			.filter(x => x.idx !== ignoreIndex && !!x.s && !!x.e)
			.some(x => this.rangesOverlap(start, end, x.s, x.e));
	}

	public formatToUIDateTimeFormat(date){
		return formatDate(date, UIDateTimeFormat.date, UIDateTimeFormat.locale)
	}

		public calculateAgeInYears(dateOfBirth: any, referenceDate: Date = new Date()): number | null {
		const dob = this.parseDate(dateOfBirth);
		if (!dob) { return null; }

		let age = referenceDate.getFullYear() - dob.getFullYear();
		const monthDiff = referenceDate.getMonth() - dob.getMonth();
		const dayDiff = referenceDate.getDate() - dob.getDate();

		// Adjust if birthday hasn't occurred yet this year
		if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
			age--;
		}

		return age;
	}

	public isUnderAge(dateOfBirth: any, ageThreshold: number, referenceDate: Date = new Date()): boolean {
		const age = this.calculateAgeInYears(dateOfBirth, referenceDate);
		return age !== null && age < ageThreshold;
	}
}
